import { environment } from '../../../environments/environment';

/** Ключ переопределения в localStorage. Живёт в профиле браузера конкретного АРМ. */
const STORAGE_KEY = 'rais.kioskMode';

/** Параметр URL запуска: `?kiosk=1` включает, `?kiosk=0` снимает. */
const QUERY_KEY = 'kiosk';

/**
 * Работает ли РАИС на этом рабочем месте в режиме киоска.
 *
 * Два источника, и оба нужны:
 *
 * - `environment.settings.kioskMode` — свойство **установки**. Файл `environment.json` отдаёт
 *   сервер, один и тот же всем АРМ, поэтому этот флаг годится для ПАК, где в киоске работают
 *   все рабочие места, и не годится там, где киоск только у койки, а в ординаторской обычный
 *   браузер.
 * - `?kiosk=1` в URL запуска — свойство **рабочего места**. Это единственное, что на АРМ
 *   своё: строка запуска браузера лежит на самом АРМ. Значение запоминается в localStorage
 *   профиля браузера, иначе оно потерялось бы на первом же переходе по маршруту.
 *
 * Переопределение сильнее флага установки в обе стороны: `?kiosk=0` снимает киоск с одного
 * АРМ в киосковой установке.
 */
export function isKioskWorkstation(): boolean {
  const override = readOverride();

  return override ?? environment.settings.kioskMode === true;
}

function readOverride(): boolean | null {
  const fromQuery = readQuery();

  if (fromQuery !== null) {
    writeStored(fromQuery);
    return fromQuery;
  }

  return readStored();
}

function readQuery(): boolean | null {
  const raw = new URLSearchParams(window.location.search).get(QUERY_KEY);

  if (raw === null) return null;

  // Пустой `?kiosk` — это «включить»: так короче в строке запуска браузера.
  if (raw === '' || raw === '1' || raw === 'true') return true;
  if (raw === '0' || raw === 'false') return false;

  return null;
}

function readStored(): boolean | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (raw === null) return null;

    return raw === '1';
  } catch {
    // Недоступный localStorage — не событие: остаётся флаг установки.
    return null;
  }
}

function writeStored(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    // См. выше. Киоск доживёт до первого перехода по маршруту и на этом кончится,
    // но ломать запуск из-за запрещённого хранилища незачем.
  }
}
